import { CommonModule } from '@angular/common';
import {
  Component,
  output,
  signal,
  OnDestroy,
  ChangeDetectionStrategy,
  computed,
} from '@angular/core';
import { TranslatePipe } from '@ngx-translate/core';

interface FileMetadata {
  file: File;
  isImage: boolean;
  extension: string;
  thumbnail: string;
  size: string;
}

@Component({
  selector: 'app-upload-media',
  standalone: true,
  imports: [CommonModule, TranslatePipe],
  templateUrl: './upload-media.component.html',
  styleUrl: './upload-media.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class UploadMediaComponent implements OnDestroy {
  uploadedFiles = signal<File[]>([]);
  isUploading = signal<boolean>(false);
  uploadProgress = signal<number>(0);
  private objectUrls = new Map<File, string>();

  // Pre-compute file metadata to avoid calling methods in template
  fileMetadata = computed<FileMetadata[]>(() => {
    return this.uploadedFiles().map((file) => ({
      file,
      isImage: this.isImageFile(file),
      extension: this.getFileExtension(file),
      thumbnail: this.getFileThumbnail(file),
      size: this.formatFileSize(file.size),
    }));
  });

  // Output to emit data changes to parent
  dataChange = output<{
    files: File[];
  }>();

  onFileSelect(event: Event): void {
    const input = event.target as HTMLInputElement;
    if (input.files && input.files.length > 0) {
      const files = Array.from(input.files);
      this.startUpload(files);
    }
  }

  onDragOver(event: DragEvent): void {
    event.preventDefault();
    event.stopPropagation();
  }

  onDrop(event: DragEvent): void {
    event.preventDefault();
    event.stopPropagation();
    if (event.dataTransfer?.files) {
      const files = Array.from(event.dataTransfer.files);
      this.startUpload(files);
    }
  }

  // Simulate file upload with progress
  private startUpload(newFiles: File[]): void {
    this.isUploading.set(true);
    this.uploadProgress.set(0);

    // Get current files and append new ones
    const currentFiles = this.uploadedFiles();
    // Filter out duplicates by name and size
    const existingFileNames = new Set(
      currentFiles.map((f) => `${f.name}-${f.size}`)
    );
    const uniqueNewFiles = newFiles.filter(
      (file) => !existingFileNames.has(`${file.name}-${file.size}`)
    );

    if (uniqueNewFiles.length === 0) {
      // No new files to add
      this.isUploading.set(false);
      return;
    }

    // Simulate upload progress
    // In a real application, this would be replaced with actual upload service
    // Use requestAnimationFrame for smoother updates and less frequent change detection
    let progress = 0;
    const updateProgress = () => {
      if (progress < 100) {
        // Increment progress (simulate)
        const increment = Math.random() * 15 + 5; // Random increment between 5-20%
        progress = Math.min(100, progress + increment);
        this.uploadProgress.set(Math.round(progress));
        requestAnimationFrame(updateProgress);
      } else {
        // Upload complete - append new files to existing ones
        this.uploadedFiles.set([...currentFiles, ...uniqueNewFiles]);
        this.isUploading.set(false);
        this.uploadProgress.set(0);
        this.emitDataChange();
      }
    };
    requestAnimationFrame(updateProgress);
  }

  // Emit data change
  private emitDataChange(): void {
    this.dataChange.emit({
      files: this.uploadedFiles(),
    });
  }

  // Format file size to human readable format
  formatFileSize(bytes: number): string {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return Math.round((bytes / Math.pow(k, i)) * 100) / 100 + sizes[i];
  }

  // Check if file is an image
  isImageFile(file: File): boolean {
    return file.type.startsWith('image/');
  }

  // Get file extension
  getFileExtension(file: File): string {
    const fileName = file.name;
    const lastDot = fileName.lastIndexOf('.');
    if (lastDot === -1) return '';
    return fileName.substring(lastDot + 1).toUpperCase();
  }

  // Get file thumbnail (for images) or placeholder
  getFileThumbnail(file: File): string {
    if (this.isImageFile(file)) {
      // Check if we already have an object URL for this file
      if (!this.objectUrls.has(file)) {
        const objectUrl = URL.createObjectURL(file);
        this.objectUrls.set(file, objectUrl);
      }
      return this.objectUrls.get(file)!;
    }
    // Return placeholder for non-image files (not used, we show custom icon)
    return '/public/svgs/file-placeholder.svg';
  }

  // Remove file from uploaded files by index
  removeFile(index: number): void {
    const files = this.uploadedFiles();
    const fileToRemove = files[index];
    this.removeFileByFile(fileToRemove);
  }

  // Remove file from uploaded files by file reference
  removeFileByFile(fileToRemove: File): void {
    // Clean up object URL if it's an image
    if (fileToRemove && this.objectUrls.has(fileToRemove)) {
      const objectUrl = this.objectUrls.get(fileToRemove);
      if (objectUrl) {
        URL.revokeObjectURL(objectUrl);
        this.objectUrls.delete(fileToRemove);
      }
    }

    const files = this.uploadedFiles();
    const updatedFiles = files.filter((f) => f !== fileToRemove);
    this.uploadedFiles.set(updatedFiles);
    this.emitDataChange();
  }

  // Handle image load error
  onImageError(event: Event): void {
    const img = event.target as HTMLImageElement;
    if (img) {
      img.src = '/public/svgs/file-placeholder.svg';
    }
  }

  ngOnDestroy(): void {
    // Clean up all object URLs to prevent memory leaks
    this.objectUrls.forEach((url) => URL.revokeObjectURL(url));
    this.objectUrls.clear();
  }
}
